// src/verticals/general/widgets/GeneralSkillsWidget.tsx
//
// "My Skills" — the professional skill areas the general (Rehearse) catalog is
// organized into (CATEGORY_META: Crisis Intervention, Therapy, …), with the
// learner's real mastery overlaid: how many of that area's programs they've
// finished. Honest by construction — the bar is completed-programs / programs,
// pulled from `user_course_progress`, never a fabricated score. Areas the
// learner hasn't started still show (at 0%) so the widget doubles as a skill map
// to grow into.
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/shared/lib/supabase";
import { useVerticalCourses } from "@/shared/components/dashboard/AICoursesSection";
import { CATEGORY_META, categoryLabel } from "@/shared/lib/catalogCategories";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { useT } from "@/shared/i18n/useT";
import { registerWidget } from "@/shared/dashboard/widgets/registry";
import type { WidgetProps } from "@/shared/dashboard/widgets/types";

interface ProgressRow {
  course_id: string;
  completed_at: string | null;
}

interface SkillArea {
  slug: string;
  label: string;
  total: number;
  completed: number;
  started: number;
}

function GeneralSkillsWidgetImpl(_: WidgetProps) {
  const t = useT();
  const { locale } = useLanguage();
  const { courses, loading: coursesLoading } = useVerticalCourses();
  const [progress, setProgress] = useState<ProgressRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        if (!cancelled) setProgress([]);
        return;
      }
      const { data, error } = await supabase
        .from("user_course_progress")
        .select("course_id, completed_at");
      if (cancelled) return;
      if (error) {
        console.error("GeneralSkillsWidget progress fetch failed:", error);
        setProgress([]);
      } else {
        setProgress((data as ProgressRow[]) ?? []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const areas = useMemo<SkillArea[]>(() => {
    if (!progress) return [];
    const completedIds = new Set(
      progress.filter((p) => p.completed_at).map((p) => p.course_id),
    );
    const startedIds = new Set(progress.map((p) => p.course_id));
    return CATEGORY_META.filter((c) => c.status === "live")
      .map((cat) => {
        const catCourses = courses.filter((c) => c.category === cat.slug);
        return {
          slug: cat.slug,
          label: categoryLabel(cat.slug, locale),
          total: catCourses.length,
          completed: catCourses.filter((c) => completedIds.has(c.id)).length,
          started: catCourses.filter((c) => startedIds.has(c.id)).length,
        };
      })
      .filter((a) => a.total > 0)
      .sort((a, b) => b.completed / b.total - a.completed / a.total);
  }, [courses, progress, locale]);

  const loading = coursesLoading || progress === null;

  return (
    <section className="rounded-md border border-[var(--hairline)] bg-[var(--paper)] p-5">
      <h3 className="mb-3 text-[15px] font-semibold text-[var(--ink)]">
        {t("dash.skills.heading")}
      </h3>

      {loading ? (
        <div className="space-y-3.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-9 animate-pulse rounded-[6px] bg-[rgba(12,26,51,0.05)]"
            />
          ))}
        </div>
      ) : areas.length === 0 ? (
        <p className="text-sm text-[var(--muted)]">{t("dash.skills.empty")}</p>
      ) : (
        <ul className="space-y-4">
          {areas.map((area) => {
            const pct =
              area.total > 0
                ? Math.round((area.completed / area.total) * 100)
                : 0;
            return (
              <li key={area.slug}>
                <div className="mb-1.5 flex items-baseline justify-between gap-3">
                  <span className="truncate text-sm font-medium text-[var(--ink)]">
                    {area.label}
                  </span>
                  <span className="ed-mono shrink-0 text-[10.5px] text-[var(--muted)]">
                    {area.completed > 0
                      ? t("dash.skills.programsDone", {
                          done: area.completed,
                          total: area.total,
                        })
                      : t("dash.skills.notStarted")}
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-[rgba(12,26,51,0.08)]">
                  <div
                    className="h-full rounded-full bg-[var(--accent)] transition-[width] duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

registerWidget({
  id: "generalSkills",
  component: GeneralSkillsWidgetImpl,
  supportedSlots: new Set(["mainColumn", "sideRail"]),
  displayName: "My Skills",
});

export const GeneralSkillsWidget = GeneralSkillsWidgetImpl;
